import { Link } from 'react-router-dom';
import { formatRelativeOrDate, Skeleton } from '@ah/design';
import { MediaThumb } from '@ah/design/mobile';
import type { EpisodeListItem } from '../shared/index.ts';

export function formatDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function formatTimestamp(seconds: number | null): string | null {
  if (seconds == null || seconds < 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function EpisodeRow({ ep }: { ep: EpisodeListItem }) {
  const image = ep.imageUrl ?? ep.series?.imageUrl ?? null;
  const duration = formatDuration(ep.duration);
  return (
    <Link
      to={`/podcasts/${ep.id}`}
      className="flex gap-3 p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <MediaThumb src={image} className="w-14 h-14 rounded-lg shrink-0" iconSize={18} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-snug line-clamp-2">{ep.name}</p>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
          {ep.series?.name ?? 'Unknown show'}
          {duration ? <span> · {duration}</span> : null}
          {ep.datePublished ? <span> · {formatRelativeOrDate(ep.datePublished)}</span> : null}
        </p>
      </div>
    </Link>
  );
}

export function EpisodeListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex gap-3 p-3 bg-card rounded-xl border border-border">
          <Skeleton className="w-14 h-14 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ShowsGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="aspect-square rounded-xl" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  );
}

export function ErrorRetry({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">{label}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}
